import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from "react";
import Cookies from "js-cookie";
import { useLocation, Navigate, useNavigate } from "react-router-dom";

//api here is an axios instance which has the baseURL set according to the env.
import { getUser } from "../api/auth";
import { getStakes } from "../api/balance";
import { useNotification } from "./notif";
import { applyStakesTotal } from "../utils/stakes";
import LoadingScreen from "../components/LoadingScreen";

export const AuthContext = createContext({});
export const AuthProvider = ({ children }) => {
  let location = useLocation();
  const navigate = useNavigate();
  const notification = useNotification();
  const addNotification = useMemo(
    () =>
      typeof notification?.addNotification === "function"
        ? notification.addNotification
        : () => {},
    [notification]
  );

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async (stakes) => {
    const token = Cookies.get("token");
    if (!token) return null;
    try {
      const response = await getUser(token);
      if (response?.data) {
        setUser(applyStakesTotal(response.data, stakes));
        return response.data;
      }
    } catch (err) {
      console.error("Failed to refresh user:", err);
    }
    return null;
  }, []);

  useEffect(() => {
    async function loadUserFromCookies() {
      const token = Cookies.get("token");
      if (token) {
        try {
          let stakes = null;
          // Settle matured stakes on dashboard load before rendering totals.
          if (location.pathname === "/dashboard") {
            try {
              const stakesResponse = await getStakes(token);
              if (Array.isArray(stakesResponse?.data)) {
                stakes = stakesResponse.data;
                const settled = stakes.filter(
                  (s) => s?.settled_now === true && !s?.settlement_error
                );
                settled.forEach((stake) => {
                  const amount = stake.payout_amount;
                  addNotification({
                    title: "Stake matured",
                    message: `Stake matured: ${amount ?? "payout"} ${stake.coin ?? ""} credited`,
                    level: "success",
                  });
                });
                const errored = stakes.filter(
                  (s) => s?.status === "matured" && s?.settlement_error
                );
                errored.forEach((stake) => {
                  console.error("Stake settlement failed:", {
                    staking_id: stake.staking_id,
                    error: stake.settlement_error,
                  });
                });
              }
            } catch (err) {
              console.error("Failed to load stakes:", err);
            }
          }
          const response = await getUser(token);
          if (response?.data) setUser(applyStakesTotal(response.data, stakes));
        } catch (err) {
          if (err?.response?.status === 403) {
            navigate("/verify-email");
          }
          if (err?.response?.status === 401) {
            navigate("/login");
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    if (
      location.pathname === "/dashboard" ||
      location.pathname === "/verify-email"
    ) {
      setLoading(true);
      loadUserFromCookies();
    } else {
      setLoading(false);
    }
  }, [location.pathname, addNotification, navigate]);

  // const login = async (email, password) => {
  //     const { data: token } = await api.post('auth/login', { email, password })
  //     if (token) {
  //         console.log("Got token")
  //         Cookies.set('token', token, { expires: 60 })
  //         api.defaults.headers.Authorization = `Bearer ${token.token}`
  //         const { data: user } = await api.get('users/me')
  //         setUser(user)
  //         console.log("Got user", user)
  //     }
  // }

  const logout = (email, password) => {
    Cookies.remove("token");
    setUser(null);
    window.location.pathname = "/login";
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!user, user, loading, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const ProtectRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (isAuthenticated === false && loading === false) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page
    return <Navigate to="/login" />;
  }
  if (loading) {
    return <LoadingScreen loading={loading} />;
  }
  return children;
};